/**
 * Autocomplete de cidades/bairros da captura de places — porta da técnica do legado
 * (`Datalist.getUrlMaps` [application.js:14269] + método "external" [14450–14573]):
 *
 *  1. PRIMÁRIO: API de typeahead da família Expedia — um endpoint sorteado entre ~30
 *     domínios (expedia.*, hoteis.com, vrbo, orbitz, …), cada um com seu guid/siteid.
 *     Resposta: `{ sr: [{ type, coordinates, regionNames, hierarchyInfo }] }`.
 *  2. FALLBACK: Photon (Komoot) — `photon.komoot.io/api/?lang=en&q=` (GeoJSON features).
 *
 * Saída: `{ geo: "lat,long", short, long }` — a tag adicionada é `geo|short`, o mesmo
 * formato que `POST /search` espera em `places[]` (e que o mapa usa para os círculos).
 *
 * Desvio consciente: o legado só cai no Photon quando o fetch LANÇA exceção (resposta
 * !ok ficava sem resultado); aqui o fallback também cobre !ok/JSON inválido.
 */

interface TypeaheadEndpoint {
  url: string;
  uid: string;
}

const ENDPOINTS: TypeaheadEndpoint[] = [
  { url: 'expedia.com.au', uid: '&guid=bc4f905a-b2ce-4225-abcd-83d377b29688&siteid=25' },
  { url: 'expedia.com.sg', uid: '&guid=2a88a76d-c3ea-4053-8347-e008518b1eac&siteid=14' },
  { url: 'expedia.com.hk', uid: '&guid=7c0413f8-cef0-4758-80d6-ec1a28ac491d&siteid=18' },
  { url: 'expedia.co.in', uid: '&guid=dae910c1-9384-4bac-b45b-8865ce7f882f&siteid=27' },
  { url: 'expedia.co.jp', uid: '&guid=44b7ef12-fbe4-48b4-a768-830529318982&siteid=28' },
  { url: 'expedia.co.kr', uid: '&guid=2511ae58-08d2-439e-b54c-255ef9301f3b&siteid=16' },
  { url: 'expedia.co.uk', uid: '&guid=391dd0ee-89c0-4e6e-9d94-2b8aca1477d1&siteid=3' },
  { url: 'expedia.com', uid: '&guid=937d4e1c-cee0-4b74-808c-b03d974ea27d&siteid=1' },
  { url: 'expedia.fr', uid: '&guid=be61d204-f7e9-4579-89e1-c19bba2397bd&siteid=20' },
  { url: 'expedia.de', uid: '&guid=d23b7715-c19f-42f1-834b-d789aa89a176&siteid=6' },
  { url: 'expedia.ca', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.ie', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.es', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.it', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.nl', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.mx', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.co.nz', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.com.br', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expedia.com.ar', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'expediacruises.com', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'lastminute.com.au', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'ebookers.com', uid: '&guid=d3515815-60ad-4e35-867d-2ffe2425a5ed&siteid=4' },
  { url: 'cheaptickets.com', uid: '&guid=c8fbf1c1-268b-4753-918a-dd2dcddd2dbc&siteid=70301' },
  { url: 'travelocity.com', uid: '&guid=4f5ed4af-9188-4638-bc55-a77c913e08e5&siteid=80001' },
  { url: 'carrentals.com', uid: '&guid=c5c62730-2e2b-467d-b732-afafd434f8bb&siteid=70801' },
  { url: 'hoteis.com', uid: '&guid=198bbaef-45a3-41a7-89c8-dbaa5e8bc36c&siteid=301800003' },
  { url: 'hotwire.com', uid: '&guid=a055b495-2f79-4394-8683-59df90ff738&siteid=30031' },
  { url: 'vrbo.com', uid: '&guid=a9ffc7c3-97fe-3967-cab9-c4a4bdaf4577&siteid=9007028' },
  { url: 'orbitz.com', uid: '&guid=03770a37-0c99-41ce-849b-f1c5da9264a&siteid=70201' },
  { url: 'wotif.com', uid: '&guid=cefcff46-dd72-4037-a181-ecf5a25d078a&siteid=70125' },
];

const KOMOOT = 'https://photon.komoot.io/api/?lang=en&q=';

/** Tipos ignorados na resposta do typeahead (legado). */
const SKIP_TYPES = ['MULTICITY', 'MULTIREGION', 'AIRPORT', 'METROCODE', 'HOTEL'];

export interface LocationPrediction {
  /** "lat,long" — vira o valor da tag (`geo|short`) e alimenta o círculo do mapa. */
  geo: string;
  /** Nome curto (exibe na tag e em negrito no dropdown). */
  short: string;
  /** Nome completo (legenda do dropdown). */
  long: string;
}

/** Locale do typeahead (porta do mapeamento do legado). */
function typeaheadLocale(language: string | undefined): string {
  let lang = (language ?? '').replace('-', '_');
  if (!lang) return 'en_US';
  if (!lang.includes('_')) {
    if (lang === 'pt') lang = 'pt_BR';
    else if (lang === 'fr') lang = 'fr_FR';
    else if (lang === 'es') lang = 'es_ES';
    else lang = 'en_US';
  }
  return lang;
}

/** Monta a URL do typeahead com endpoint sorteado (porta exata de getUrlMaps). */
export function buildTypeaheadUrl(term: string, language?: string): string {
  const endpoint = ENDPOINTS[Math.floor(Math.random() * ENDPOINTS.length)]!;
  let url = 'https://www.' + endpoint.url;
  url += '/api/v4/typeahead/' + encodeURI(term);
  url += '?browser=Chrome&client=Homepage&expuserid=-1&lob=HOTELS';
  url += '&personalize=true&trending=true&ab=42716.1&regiontype=2047';
  url += '&format=json&dest=true&device=Desktop&maxresults=8&locale=' + typeaheadLocale(language);
  url += endpoint.uid;
  return url;
}

interface TypeaheadEntry {
  type?: string;
  coordinates?: { lat?: number | string; long?: number | string };
  regionNames?: { shortName?: string; fullName?: string; displayName?: string };
  hierarchyInfo?: { country?: { name?: string; isoCode2?: string } };
}

interface PhotonFeature {
  geometry?: { coordinates?: number[] };
  properties?: { name?: string; state?: string; country?: string; countrycode?: string };
}

function parseTypeahead(data: { sr?: TypeaheadEntry[] }): LocationPrediction[] {
  const predictions: LocationPrediction[] = [];
  for (const source of data.sr ?? []) {
    if (source.type && SKIP_TYPES.includes(source.type)) continue;
    const geo =
      source.coordinates?.lat != null && source.coordinates.long != null
        ? `${source.coordinates.lat},${source.coordinates.long}`
        : '';
    const short = source.regionNames?.shortName ?? '';
    const long = source.regionNames?.fullName ?? '';
    const display = source.regionNames?.displayName ?? '';
    const country = source.hierarchyInfo?.country?.name ?? '';
    const iso = source.hierarchyInfo?.country?.isoCode2 ?? '';
    if (geo && short && long && display && country && iso) {
      predictions.push({ geo, short, long });
    }
  }
  return predictions;
}

function parsePhoton(data: { features?: PhotonFeature[] }): LocationPrediction[] {
  const predictions: LocationPrediction[] = [];
  for (const feature of data.features ?? []) {
    const coords = feature.geometry?.coordinates;
    const geo = coords && coords.length >= 2 ? `${coords[1]},${coords[0]}` : '';
    const props = feature.properties;
    const short = props?.name ?? '';
    let long = short;
    if (props?.state) long += ', ' + props.state;
    if (props?.country) long += ', ' + props.country;
    const country = props?.country ?? '';
    const iso = props?.countrycode ?? '';
    if (geo && short && long && country && iso) {
      predictions.push({ geo, short, long });
    }
  }
  return predictions;
}

async function fetchPhoton(term: string, signal?: AbortSignal): Promise<LocationPrediction[]> {
  const res = await fetch(KOMOOT + encodeURI(term), { signal });
  if (!res.ok) return [];
  return parsePhoton((await res.json()) as { features?: PhotonFeature[] });
}

/**
 * Busca localizações: typeahead Expedia (endpoint sorteado) com fallback Photon.
 */
export async function fetchLocations(
  term: string,
  language?: string,
  signal?: AbortSignal,
): Promise<LocationPrediction[]> {
  try {
    const res = await fetch(buildTypeaheadUrl(term, language), { signal });
    if (res.ok) {
      const data = (await res.json()) as { sr?: TypeaheadEntry[] };
      if (data && data.sr) return parseTypeahead(data);
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    // typeahead indisponível → Photon
  }
  try {
    return await fetchPhoton(term, signal);
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    return [];
  }
}
